// JSON fetch wrapper. Tracks the CSRF token via the X-CSRF-Token response
// header from prior safe requests (the __Host-csrf cookie is HttpOnly per
// the middleware comment in internal/csrf/middleware.go, so document.cookie
// can't read it). Falls back to a document.cookie read for tests that mint
// non-HttpOnly cookies. All API endpoints expect JSON; cursor params (if
// any) ride in the request body (POST-for-complex-read) per WAF Rule 1.

import { getAccessToken } from './auth';
import { handleCompromiseSignal } from './compromise';

const CSRF_COOKIE = '__Host-csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const COMPROMISE_HEADER = 'X-Compromise-Signal';

let cachedCsrfToken: string | null = null;

function csrfToken(): string {
  if (cachedCsrfToken) return cachedCsrfToken;
  // Fallback for environments where the cookie is reachable from JS
  // (older dev http config, tests). Returns empty when the cookie is
  // HttpOnly — that's the production case and the response-header
  // capture below is what we actually rely on.
  const m = document.cookie.match(new RegExp('(?:^|; )' + CSRF_COOKIE.replace(/\$/g, '\\$') + '=([^;]+)'));
  return m ? decodeURIComponent(m[1]) : '';
}

type ApiOpts = {
  method?: string;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function api<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const token = csrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }
  // vfco-auth-package's Protected middleware reads the short-lived JWT
  // from Authorization. Attach when we have one; the auth-package's own
  // public endpoints (/api/auth/magic-link, /api/auth/verify, /api/auth/refresh)
  // ignore the header.
  const access = getAccessToken();
  if (access) headers['Authorization'] = 'Bearer ' + access;
  if (opts.idempotencyKey) {
    headers['Idempotency-Key'] = opts.idempotencyKey;
  }
  // Caller-supplied headers last so they can set request-specific values
  // (e.g. X-Device-Class on refresh) without overriding the auth/CSRF wiring.
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) headers[k] = v;
  }
  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
  // Capture the CSRF token from the response header on every request —
  // the middleware echoes the current token on each response so JS can
  // submit it on subsequent unsafe methods without reading the HttpOnly
  // cookie directly.
  const headerToken = res.headers.get(CSRF_HEADER);
  if (headerToken) cachedCsrfToken = headerToken;
  // a compromised-device 401 carries this header. Trigger the offline
  // wipe + redirect here (the central response path) so any endpoint's signal
  // is honoured, not just /api/me. The 401 still throws below for the caller.
  if (res.headers.get(COMPROMISE_HEADER) === 'true') {
    handleCompromiseSignal();
  }
  const text = await res.text();
  const json = text ? safeJSON(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, json, `api ${method} ${path}: ${res.status}`);
  }
  return json as T;
}

function safeJSON(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}

// isDesktopRequired reports whether a caught error is the server's desktop-only
// key-gate denial (403 with code DESKTOP_REQUIRED, from rbac.RequireDesktop /
// RequireDesktopLookup). It is the signal to render the friendly "use a
// desktop/laptop" state instead of a generic "please try again" that can never
// succeed from this device.
export function isDesktopRequired(err: unknown): boolean {
  return err instanceof ApiError && (err.body as { code?: unknown } | null)?.code === 'DESKTOP_REQUIRED';
}

export type DownloadResult = { blob: Blob; filename: string };

// apiDownload is the blob sibling of api<T>: it runs the same auth + CSRF +
// compromise-signal wiring, but returns the response body as a Blob plus the
// server-provided download filename (from Content-Disposition) instead of parsing
// JSON. Used by the one-click DSAR CSV export — the response is text/csv, and the
// download must still carry the Authorization + CSRF headers, so it can't be a
// plain <a href> link. A non-2xx response is read as JSON/text and thrown as an
// ApiError, matching api<T>.
export async function apiDownload(path: string, opts: ApiOpts = {}): Promise<DownloadResult> {
  const method = opts.method ?? 'POST';
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const token = csrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }
  const access = getAccessToken();
  if (access) headers['Authorization'] = 'Bearer ' + access;
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) headers[k] = v;
  }
  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
  const headerToken = res.headers.get(CSRF_HEADER);
  if (headerToken) cachedCsrfToken = headerToken;
  if (res.headers.get(COMPROMISE_HEADER) === 'true') {
    handleCompromiseSignal();
  }
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text ? safeJSON(text) : null, `api ${method} ${path}: ${res.status}`);
  }
  const blob = await res.blob();
  return { blob, filename: filenameFromDisposition(res.headers.get('Content-Disposition')) };
}

// filenameFromDisposition pulls the download filename out of a Content-Disposition
// header (e.g. `attachment; filename="dsar-export-20261031.csv"`), or returns ""
// when absent so the caller can fall back to a default.
function filenameFromDisposition(cd: string | null): string {
  if (!cd) return '';
  const m = /filename="?([^";]+)"?/.exec(cd);
  return m ? m[1] : '';
}
